"use client"

import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion"
import { useRef, type MouseEvent, type ReactNode } from "react"

import { cn } from "@/lib/utils"

/**
 * Motion vocabulary for EditorialHero.
 *   - Image tile: scroll-linked subtle scale + mouse-tilt (2.5° subtle)
 *   - Glow background: continuous ambient pulse + scroll drift
 *   - Magnetic CTA wrapper
 *   - Metric cards: hover-lift via wrapper
 */

export function EditorialHeroImageWrap({ children, className }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] })
  // PUSHED HARDER: dramatic scroll-linked scale + drift. Was subtle 1.06↔1.0,
  // now 1.18↔0.96 — image breathes visibly through the section.
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.18, 0.96, 1.18])
  const y = useTransform(scrollYProgress, [0, 1], [40, -40])

  const tiltX = useMotionValue(0)
  const tiltY = useMotionValue(0)
  // PUSHED HARDER: 12° max tilt (was 5°). Cinematic 3D depth, not "polite hover".
  const rotateX = useSpring(tiltX, { stiffness: 180, damping: 22, mass: 0.4 })
  const rotateY = useSpring(tiltY, { stiffness: 180, damping: 22, mass: 0.4 })

  function handleMove(e: MouseEvent<HTMLDivElement>) {
    const el = ref.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    tiltY.set(((e.clientX - rect.left) / rect.width - 0.5) * 12)
    tiltX.set(-((e.clientY - rect.top) / rect.height - 0.5) * 12)
  }
  function handleLeave() {
    tiltX.set(0)
    tiltY.set(0)
  }

  // CRITICAL FIX: apply caller's className directly to the motion.div (which
  // owns the perspective + transforms) instead of wrapping in an extra div.
  // The caller's className has min-h-[28rem] which gives the motion.div real
  // height; the absolute Image inside (fill) now has something to fill.
  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      style={{ scale, y, rotateX, rotateY, transformStyle: "preserve-3d", perspective: 1400 }}
      className={cn("relative", className)}
    >
      {children}
    </motion.div>
  )
}

export function EditorialHeroAmbientGlow({ className }: { className?: string }) {
  return (
    <motion.div
      aria-hidden="true"
      className={cn("absolute -z-10 rounded-full bg-primary/12 blur-3xl", className)}
      animate={{ scale: [1, 1.15, 1], opacity: [0.6, 1, 0.6] }}
      transition={{ duration: 6.5, ease: "easeInOut", repeat: Infinity }}
    />
  )
}

export function EditorialHeroMagneticCta({ children, className }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null)
  const x = useMotionValue(0)
  const y = useMotionValue(0)
  const sx = useSpring(x, { stiffness: 220, damping: 20, mass: 0.3 })
  const sy = useSpring(y, { stiffness: 220, damping: 20, mass: 0.3 })

  function handleMove(e: MouseEvent<HTMLDivElement>) {
    const el = ref.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    x.set(((e.clientX - rect.left) / rect.width - 0.5) * 12)
    y.set(((e.clientY - rect.top) / rect.height - 0.5) * 8)
  }
  function handleLeave() {
    x.set(0)
    y.set(0)
  }

  return (
    <motion.div ref={ref} onMouseMove={handleMove} onMouseLeave={handleLeave} style={{ x: sx, y: sy }} className={cn("inline-flex", className)}>
      {children}
    </motion.div>
  )
}

export function EditorialHeroMetricLift({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <motion.div
      whileHover={{ y: -4, scale: 1.03, transition: { type: "spring", stiffness: 280, damping: 22 } }}
      className={cn("h-full", className)}
    >
      {children}
    </motion.div>
  )
}
